import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"

const API = process.env.REACT_APP_API_URL || "http://localhost:8000"

/**
 * OnboardingFlow — shown to new users after registration.
 * 3 steps: select exchanges → set threshold → connect Telegram
 * Saves settings and redirects to Dashboard when complete.
 */
export default function OnboardingFlow() {
    const nav = useNavigate()
    const [step, setStep] = useState(1)
    const [markets, setMarkets] = useState(["binance", "bybit"])
    const [threshold, setThreshold] = useState(0.3)
    const [tgLoading, setTgLoading] = useState(false)
    const [tgStatus, setTgStatus] = useState("")
    const BOT_USERNAME = "MerabaCryptoBot"

    const token = localStorage.getItem("token")
    const headers = { Authorization: `Bearer ${token}` }

    const toggleMarket = (m) =>
        setMarkets(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])

    const saveAndNext = async () => {
        if (step === 2) {
            try {
                await axios.post(
                    `${API}/settings`,
                    { markets },
                    { headers }
                )
            } catch (err) {
                console.warn("settings save failed (non-fatal):", err)
            }
            try {
                await axios.post(
                    `${API}/alerts/prefs`,
                    { spread_threshold: threshold},
                    { headers }
                )
            } catch (err) {
                console.warn("alert prefs save failed (non-fatal):", err)
            }
       }
       if (step === 3) {
            nav("/dashboard")
            return
        }
        setStep(s => s + 1)
    }

    const connectTelegram = async () => {
        setTgStatus("")
        setTgLoading(true)
        try {
            const res = await axios.post(`${API}/telegram/link`, {}, { headers })
            window.open(res.data.url, "_blank", "noopener")
            setTgStatus("Open Telegram, press Start, then click Continue below.")
        } catch (err) {
            setTgStatus(err.response?.data?.detail || "Could not generate link. Try from Settings after onboarding.")
        } finally {
            setTgLoading(false)
        }
    }

    const EXCHANGES = ["binance", "bybit", "kraken", "okx", "coinbase"]

    const steps = [
        { label: "Exchanges", num: 1 },
        { label: "Threshold", num: 2 },
        { label: "Telegram",  num: 3 },
    ]

    return (
        <div style={styles.page}>
            <div style={styles.card}>

                {/* Progress */}
                <div style={styles.progress}>
                    {steps.map(s => (
                        <div key={s.num} style={styles.stepItem}>
                            <div style={{
                                ...styles.stepDot,
                                background: step >= s.num ? "#00ffc6" : "#1e2a4a",
                                color:      step >= s.num ? "#060b23" : "#6b7280",
                            }}>
                                {step > s.num ? "✓" : s.num}
                            </div>
                            <span style={{ fontSize: 12, color: step >= s.num ? "#00ffc6" : "#6b7280" }}>
                                {s.label}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Step 1 — Select exchanges */}
                {step === 1 && (
                    <div>
                        <h2 style={styles.title}>Which exchanges do you trade on?</h2>
                        <p style={styles.sub}>Select all that apply. You can change this later.</p>
                        <div style={styles.exchangeGrid}>
                            {EXCHANGES.map(ex => (
                                <button
                                    key={ex}
                                    onClick={() => toggleMarket(ex)}
                                    style={{
                                        ...styles.exBtn,
                                        background:   markets.includes(ex) ? "rgba(0,255,198,0.1)" : "#0d1433",
                                        borderColor:  markets.includes(ex) ? "#00ffc6" : "rgba(255,255,255,0.1)",
                                        color:        markets.includes(ex) ? "#00ffc6" : "#9ca3af",
                                    }}
                                >
                                    {ex.charAt(0).toUpperCase() + ex.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 2 — Alert threshold */}
                {step === 2 && (
                    <div>
                        <h2 style={styles.title}>Minimum spread to alert you</h2>
                        <p style={styles.sub}>
                            Only opportunities above this threshold trigger alerts.
                            Recommended: 0.3% — below this, fees eat most of the profit.
                        </p>
                        <div style={{ textAlign: "center", margin: "32px 0" }}>
                            <span style={{ fontSize: 48, fontWeight: 700, color: "#00ffc6" }}>
                                {threshold}%
                            </span>
                        </div>
                        <input
                            type="range"
                            min="0.1" max="2.0" step="0.1"
                            value={threshold}
                            onChange={e => setThreshold(parseFloat(e.target.value))}
                            style={{ width: "100%", accentColor: "#00ffc6" }}
                        />
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                            <span>0.1% (many alerts)</span>
                            <span>2.0% (rare, high quality)</span>
                        </div>
                    </div>
                )}

                {/* Step 3 — Telegram */}
                {step === 3 && (
                    <div>
                    	<h2 style={styles.title}>Connect Telegram alerts</h2>
        		<p style={styles.sub}>
		            Get instant notifications when a profitable spread appears.
        		</p>
        		<div style={styles.telegramBox}>
            			<p style={{ color: "#9ca3af", fontSize: 14, marginBottom: 16, textAlign: "center" }}>
                		Click below to open Telegram and link your account to @{BOT_USERNAME}:
            		</p>
            		<button
                		onClick={connectTelegram}
                		disabled={tgLoading}
                		style={styles.tgConnectBtn}
            		>
                		{tgLoading ? "Opening…" : "Connect Telegram"}
            		</button>
            		{tgStatus && (
                		<p style={{ color: "#00ffc6", fontSize: 13, marginTop: 14, textAlign: "center" }}>
                    			{tgStatus}
                		</p>
            		)}
        	    </div>
        	    <p style={{ color: "#6b7280", fontSize: 13, textAlign: "center", marginTop: 16 }}>
            	    	You can also skip this and connect Telegram later in Settings.
        	    </p>
    		</div>
	)}

                {/* Next button */}
                <button
                    onClick={saveAndNext}
                    style={styles.nextBtn}
                    disabled={step === 1 && markets.length === 0}
                >
                    {step === 3 ? "Go to Dashboard →" : "Continue →"}
                </button>

                {step < 3 && (
                    <button onClick={() => nav("/dashboard")} style={styles.skipBtn}>
                        Skip setup
                    </button>
                )}

            </div>
        </div>
    )
}

const styles = {
    page: {
        minHeight: "100vh",
        background: "#060b23",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
    },
    card: {
        background: "#0d1433",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 16,
        padding: "40px 36px",
        width: "100%",
        maxWidth: 480,
    },
    progress: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 36,
        position: "relative",
    },
    stepItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        flex: 1,
    },
    stepDot: {
        width: 32, height: 32,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 600,
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        color: "#fff",
        marginBottom: 8,
    },
    sub: {
        fontSize: 14,
        color: "#6b7280",
        marginBottom: 24,
        lineHeight: 1.6,
    },
    exchangeGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10,
    },
    exBtn: {
        padding: "12px 16px",
        borderRadius: 10,
        border: "1px solid",
        fontSize: 14,
        fontWeight: 500,
        cursor: "pointer",
        transition: "all .15s",
    },
    telegramBox: {
        background: "#060b23",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 12,
        padding: 24,
    },
    botName: {
        display: "block",
        background: "#1e2a4a",
        color: "#00ffc6",
        padding: "10px 16px",
        borderRadius: 8,
        fontSize: 14,
        fontFamily: "monospace",
    },
    tgConnectBtn: {
        width: "100%",
        background: "#00ffc6",
        color: "#060b23",
        border: "none",
        padding: "12px",
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
    },
    nextBtn: {
        width: "100%",
        background: "#00ffc6",
        color: "#060b23",
        border: "none",
        padding: "14px",
        borderRadius: 10,
        fontSize: 15,
        fontWeight: 600,
        cursor: "pointer",
        marginTop: 32,
    },
    skipBtn: {
        width: "100%",
        background: "transparent",
        color: "#4b5563",
        border: "none",
        padding: "10px",
        fontSize: 13,
        cursor: "pointer",
        marginTop: 8,
    },
}
